import { addMonths, startOfDay } from "date-fns";
import {
  DocumentData,
  FiltroSedeECorso,
  GruppoCorso,
  GruppoDipendente,
  KpiAttestato,
  StatoAttestato,
} from "../../types";

// Funzioni pure della pipeline attestati, condivise dalle tab di ricerca.
// Lo stato arriva dal backend nel campo `stato` e l'ordine della lista è
// quello canonico del backend: qui si filtra e si raggruppa, mai si decide se
// un attestato è in scadenza o scaduto, mai si riordina.

// Codice numerico dello stato nell'URL (`?status=N`) e nella select STATO:
// è il contratto storico dei link della dashboard. "superato" non ha un
// codice proprio perché non è un filtro selezionabile.
const CODICE_PER_STATO: Record<StatoAttestato, number> = {
  valido: 1,
  in_scadenza: 2,
  scaduto: 3,
  mancante: 4,
  superato: 3,
};

export function codiceStato(stato: StatoAttestato): number {
  return CODICE_PER_STATO[stato];
}

export function statoDaCodice(codice: number): StatoAttestato | undefined {
  switch (codice) {
    case 1:
      return "valido";
    case 2:
      return "in_scadenza";
    case 3:
      return "scaduto";
    case 4:
      return "mancante";
    default:
      return undefined;
  }
}

// Il codice di stato portato dall'indirizzo (`?status=N`); 0 = nessun filtro.
export function codiceStatoDaUrl(params: URLSearchParams): number {
  return +(params.get("status") || 0);
}

// La finestra degli in scadenza portata dall'indirizzo (`?expire=M`, in
// mesi); senza parametro vale 1 mese.
export function finestraDaUrl(params: URLSearchParams): number {
  const expire = params.get("expire");
  return expire ? parseInt(expire) : 1;
}

// La destinazione del click su un KPI: la tab Scadenza filtrata sullo stato
// del KPI; per gli in scadenza porta anche la finestra (1, 2 o 6 mesi) scelta.
export function urlKpi(kpi: KpiAttestato, mesi?: number): string {
  const params = new URLSearchParams({
    tab: "scadenza",
    status: String(codiceStato(kpi)),
  });
  if (kpi === "in_scadenza" && mesi !== undefined) {
    params.set("expire", String(mesi));
  }
  return `/attestati?${params.toString()}`;
}

// Forma di confronto di ogni ricerca libera: minuscolo, senza spazi ai bordi
// e con gli spazi interni collassati a uno solo. Serve su entrambi i lati del
// confronto, perche' i dati archiviati portano spazi di troppo (`"MATTEO "`)
// e il termine digitato porta le maiuscole e gli spazi dell'utente: senza
// normalizzare il termine, un catalogo tutto maiuscolo non si trova mai.
export function normalizzaTesto(testo: string | undefined | null): string {
  return (testo ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

// Il confronto usato da tutte le caselle di ricerca libera: entrambi i lati
// passano da `normalizzaTesto`, e il campo corrisponde se contiene ognuna delle
// parole cercate, in qualunque ordine, anche come parte di una parola. Cosi'
// "soccorso primo" trova PRIMO SOCCORSO e "sicurezza alto" trova SICUREZZA
// LAVORATORI - RISCHIO ALTO. Il testo vuoto non ha parole, quindi corrisponde
// sempre.
export function contieneTesto(
  campo: string | undefined | null,
  testo: string
): boolean {
  const normalizzato = normalizzaTesto(campo);
  return normalizzaTesto(testo)
    .split(" ")
    .every((parola) => normalizzato.includes(parola));
}

// Il confronto per frase intera: il campo deve contenere il testo cercato cosi'
// com'e', parole nello stesso ordine e contigue. Serve dove il testo non e'
// digitato ma scelto da una tendina.
function contieneFrase(
  campo: string | undefined | null,
  frase: string
): boolean {
  return normalizzaTesto(campo).includes(normalizzaTesto(frase));
}

// Sede di lavoro: confronto per contenuto, come la select SEDE AZIENDALE ha
// sempre fatto. Resta a frase intera e non passa da `contieneTesto`: il valore
// arriva dalla tendina ed e' il nome di una sede, non parole da cercare. Per
// parole, la voce "SAN GIOVANNI" troverebbe anche una sede "GIOVANNI SAN ..." e
// la tendina mostrerebbe attestati di una sede che non e' quella scelta.
function haSede(a: DocumentData, sede: string): boolean {
  return contieneFrase(a.metadata.sede_azienda, sede);
}

// Corso: il testo cercato (o il corso scelto da link) si confronta solo con il
// nome a catalogo del corso collegato al record, mai con il titolo stampato
// sull'attestato. Un record il cui codice corso non è a catalogo non
// corrisponde mai.
function haCorso(
  a: DocumentData,
  testo: string,
  catalogo: Map<string, string>
): boolean {
  const nomeCatalogo = catalogo.get(a.metadata.codice_corso);
  return nomeCatalogo !== undefined && contieneTesto(nomeCatalogo, testo);
}

// Filtro per sede di lavoro. Testo vuoto: nessun filtro.
export function filtraPerSede(
  attestati: DocumentData[],
  sede: string
): DocumentData[] {
  if (!sede) return attestati;
  return attestati.filter((a) => haSede(a, sede));
}

// Filtro per corso a catalogo. Testo vuoto: nessun filtro.
export function filtraPerCorso(
  attestati: DocumentData[],
  testo: string,
  catalogo: Map<string, string>
): DocumentData[] {
  if (!testo) return attestati;
  return attestati.filter((a) => haCorso(a, testo, catalogo));
}

// I due filtri di testata comuni alle tab di ricerca, in AND, in un solo
// passaggio sulla lista. Filtri entrambi vuoti: nessun filtro.
export function filtraPerSedeECorso(
  attestati: DocumentData[],
  { sede, corso }: FiltroSedeECorso,
  catalogo: Map<string, string>
): DocumentData[] {
  if (!sede && !corso) return attestati;
  return attestati.filter(
    (a) => (!sede || haSede(a, sede)) && (!corso || haCorso(a, corso, catalogo))
  );
}

// Chiave di una sede di lavoro: l'anagrafica consegna la stessa sede scritta
// in modi che a schermo sembrano identici (spazi di troppo, maiuscole
// diverse), quindi la chiave normalizza per far collassare quelle varianti.
// È anche il valore che la select passa al filtro: la forma senza spazi ai
// bordi resta contenuta in tutte le varianti, così il confronto per contenuto
// di `haSede` continua a trovare gli attestati di ognuna. Due nomi davvero
// diversi (`CALENZANO` e `CALENZANO (FI)`) restano due voci: quello è
// bonifica dell'anagrafica lato backend.
// Accetta anche la sede assente: l'anagrafica ammette dipendenti senza sede di
// lavoro, e il backend la consegna come `null`. La chiave di chi non ce l'ha e'
// la stringa vuota, che i chiamanti riconoscono come "sede non indicata".
export function chiaveSede(sede?: string | null): string {
  return (sede ?? "").trim().toUpperCase();
}

// Voci della select SEDE AZIENDALE: la chiave vuota porta l'etichetta "tutte"
// (localizzata dal chiamante), ed è anche ciò che la select mostra a riposo,
// quando il valore è la stringa vuota. Le sedi seguono nell'ordine in cui le
// consegna il backend, e ognuna si mostra con la prima occorrenza ricevuta:
// le varianti successive della stessa chiave non aggiungono una voce né
// cambiano l'etichetta già scelta.
export function opzioniSede(
  sediDiLavoro: string[],
  etichettaTutte: string
): Record<string, string> {
  const opzioni: Record<string, string> = { "": etichettaTutte };
  sediDiLavoro.forEach((sede) => {
    const chiave = chiaveSede(sede);
    if (!chiave || chiave in opzioni) return;
    opzioni[chiave] = sede;
  });
  return opzioni;
}

// Ricerca libera sul discente: trova sia "cognome nome" sia "nome cognome".
// Testo vuoto: nessun filtro.
export function filtraPerTesto(
  attestati: DocumentData[],
  testo: string
): DocumentData[] {
  if (!testo) return attestati;
  return attestati.filter((a) => {
    const cognome = a.metadata.cognome_discente ?? "";
    const nome = a.metadata.nome_discente ?? "";
    return (
      contieneTesto(`${cognome} ${nome}`, testo) ||
      contieneTesto(`${nome} ${cognome}`, testo)
    );
  });
}

// Il nome con cui una riga presenta il suo corso: il nome a catalogo del
// corso collegato al record e, se il codice non è a catalogo, il titolo
// stampato sull'attestato. È lo stesso nome dei gruppi della tab Corso, così
// un corso si chiama allo stesso modo in tutte le tab: il titolo stampato
// varia da un attestato all'altro dello stesso corso ("... RISCHIO BASSO" e
// "... RISCHIO BASSO VC").
// Sulla riga prenotata resta il corso della riga, non il titolo della
// prenotazione: stato, scadenza e attestato da scaricare sono di quel corso,
// e il corso prenotato può essere un altro (l'aggiornamento). Il titolo della
// prenotazione intitola solo il gruppo dei prenotati nella tab Corso, che si
// raggruppa proprio sul corso prenotato (`titoloGruppo`).
// Serve solo a ciò che si mostra e si cerca: prenotazione e nome del file
// scaricato continuano a leggere codice e titolo del record.
export function nomeCorso(
  a: DocumentData,
  catalogo: Map<string, string>
): string {
  return (
    catalogo.get(a.metadata.codice_corso) ?? a.metadata.titolo_corso ?? ""
  );
}

// Ricerca sul nome del corso che la riga mostra (`nomeCorso`): è la ricerca
// dentro l'accordion di un dipendente. Cercando sul titolo stampato l'utente
// vedrebbe un nome e la ricerca ne userebbe un altro. Testo vuoto: nessun
// filtro.
export function filtraPerTitoloCorso(
  attestati: DocumentData[],
  testo: string,
  catalogo: Map<string, string>
): DocumentData[] {
  if (!testo) return attestati;
  return attestati.filter((a) => contieneTesto(nomeCorso(a, catalogo), testo));
}

// Filtro per stato: confronto esatto con il campo `stato` del backend. Un
// superato non è uno scaduto né un in scadenza. Stato assente: nessun filtro.
export function filtraPerStato(
  attestati: DocumentData[],
  stato: StatoAttestato | undefined
): DocumentData[] {
  if (!stato) return attestati;
  return attestati.filter((a) => a.stato === stato);
}

// Restringe alla finestra che va da oggi a oggi più uno, due o sei mesi,
// estremi compresi: la stessa fascia dei conteggi in dashboard. Non decide lo
// stato (un valido che scade fra due mesi non esiste: lo decide il backend),
// ma è corretta anche da sola perché applica entrambi gli estremi. Un record
// senza scadenza non rientra in nessuna finestra.
export function filtraPerFinestraScadenza(
  attestati: DocumentData[],
  mesi: number,
  oggi: Date = new Date()
): DocumentData[] {
  const inizio = startOfDay(oggi).getTime();
  const limite = startOfDay(addMonths(oggi, mesi)).getTime();
  return attestati.filter((a) => {
    if (!a.metadata.data_scadenza) return false;
    const scadenza = startOfDay(new Date(a.metadata.data_scadenza)).getTime();
    return !Number.isNaN(scadenza) && scadenza >= inizio && scadenza <= limite;
  });
}

function raggruppa(
  attestati: DocumentData[],
  chiave: (a: DocumentData) => string
): Map<string, DocumentData[]> {
  const gruppi = new Map<string, DocumentData[]>();
  attestati.forEach((a) => {
    const k = chiave(a);
    const gruppo = gruppi.get(k);
    if (gruppo) {
      gruppo.push(a);
    } else {
      gruppi.set(k, [a]);
    }
  });
  return gruppi;
}

// Chiave di raggruppamento per corso: un attestato prenotato si raggruppa sul
// corso prenotato (il corso che si andrà a seguire), che sulla riga prenotata
// il backend consegna in reservationCode; gli altri sul codice del corso. Chi
// ha prenotato lo stesso corso sta nello stesso gruppo, qualunque corso la sua
// riga proponesse. I gruppi e le righe dentro ogni gruppo conservano
// l'ordine di arrivo, cioè quello canonico del backend.
// Un attestato fuori catalogo arriva senza codice corso: si raggruppa sul
// titolo stampato sull'attestato, normalizzato, così ogni corso fuori catalogo
// ha il suo gruppo invece di finire tutti in quello del primo. Il prefisso
// tiene la chiave fuori dallo spazio dei codici: un corso fuori catalogo
// intitolato come un codice a catalogo resta un gruppo a sé.
const PREFISSO_FUORI_CATALOGO = "fuori-catalogo:";

export function chiaveCorso(a: DocumentData): string {
  const prenotato = a.metadata.data_prenotazione !== undefined;
  if (prenotato && a.reservationCode) return a.reservationCode;
  if (a.metadata.codice_corso) return a.metadata.codice_corso;
  return PREFISSO_FUORI_CATALOGO + normalizzaTesto(a.metadata.titolo_corso);
}

// Il codice del corso di un gruppo: la chiave stessa, tranne per un gruppo
// fuori catalogo, che un codice non ce l'ha.
function codiceGruppo(chiave: string): string {
  return chiave.startsWith(PREFISSO_FUORI_CATALOGO) ? "" : chiave;
}

export function raggruppaPerCorso(
  attestati: DocumentData[]
): Map<string, DocumentData[]> {
  return raggruppa(attestati, chiaveCorso);
}

// Raggruppamento per dipendente sul codice fiscale del discente. I corsi
// mancanti non ancora assegnati non hanno discente e restano fuori.
export function raggruppaPerDipendente(
  attestati: DocumentData[]
): Map<string, DocumentData[]> {
  return raggruppa(
    attestati.filter((a) => !!a.metadata.cf_discente),
    (a) => a.metadata.cf_discente
  );
}

// Il titolo di un gruppo: per un gruppo di prenotati è il titolo della
// prenotazione, che il backend consegna già risolto sul record. Altrimenti è
// il nome a catalogo del corso su cui si raggruppa; se non è a catalogo si
// ripiega sul nome della riga (`nomeCorso`): il corso dell'attestato a
// catalogo e, da ultimo, il titolo stampato sull'attestato. È il caso dei
// gruppi fuori catalogo, che si raggruppano proprio su quel titolo.
function titoloGruppo(
  codice: string,
  primo: DocumentData,
  catalogo: Map<string, string>
): string {
  return (
    primo.titoloPrenotazione ??
    catalogo.get(codice) ??
    nomeCorso(primo, catalogo)
  );
}

// I gruppi della tab Corso in ordine alfabetico di titolo: è l'ordine degli
// accordion, non dei record. Dentro ogni gruppo le righe restano nell'ordine
// canonico del backend.
export function gruppiPerCorso(
  attestati: DocumentData[],
  catalogo: Map<string, string>
): GruppoCorso[] {
  return Array.from(raggruppaPerCorso(attestati).entries())
    .map(([chiave, righe]) => {
      const codice = codiceGruppo(chiave);
      return {
        chiave,
        codice,
        titolo: titoloGruppo(codice, righe[0], catalogo),
        attestati: righe,
      };
    })
    .sort((a, b) => a.titolo.localeCompare(b.titolo));
}

// Priorità dello stato (più basso = più grave) per scegliere l'attestato che
// rappresenta un dipendente nell'intestazione: il superato è storico già
// rimpiazzato e non pesa.
const PRIORITA_STATO: Record<StatoAttestato, number> = {
  mancante: 0,
  scaduto: 1,
  in_scadenza: 2,
  valido: 3,
  superato: 4,
};

function piuGrave(righe: DocumentData[]): DocumentData {
  return righe.reduce((peggiore, a) =>
    PRIORITA_STATO[a.stato] < PRIORITA_STATO[peggiore.stato] ? a : peggiore
  );
}

function confrontaTesto(a: string | undefined, b: string | undefined): number {
  return (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" });
}

// I gruppi della tab Dipendente in ordine di cognome, nome e codice fiscale:
// è l'ordine degli accordion, non dei record (il backend ordina per stato
// prima che per discente). Ogni dipendente si presenta con l'attestato nello
// stato più grave; a parità di stato vale il primo nell'ordine del backend.
// Dentro ogni gruppo le righe restano nell'ordine canonico del backend.
export function gruppiPerDipendente(
  attestati: DocumentData[]
): GruppoDipendente[] {
  return Array.from(raggruppaPerDipendente(attestati).entries())
    .map(([cf, righe]) => ({
      cf,
      rappresentativo: piuGrave(righe),
      attestati: righe,
    }))
    .sort((a, b) => {
      const da = a.rappresentativo.metadata;
      const db = b.rappresentativo.metadata;
      return (
        confrontaTesto(da.cognome_discente, db.cognome_discente) ||
        confrontaTesto(da.nome_discente, db.nome_discente) ||
        confrontaTesto(a.cf, b.cf)
      );
    });
}
